import { Backdrop, Box, CircularProgress, Link } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as constants from "../../../../constants";
import environment from "../../../../environments/environment";
import CustomAutocomplete from "../../../../shared/AutoComplete";
import ContainedButton from "../../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../../shared/Button/customOutlinedButton";
import CustomDatePicker from "../../../../shared/CustomDate";
import ReusableTable from "../../../../shared/ReusableTable/ReusableTable";
import { RootState } from "../../../../store/store";
import { resetTableState, setPagination, setTableFilters } from "../../../../store/tableSlice";
import PostDailyTransactionsPopUp from "../../../PopUp/PostDailyTransactionsPopUp";
import { axiosInterceptor } from "../../services/authInterceptor";
import basestyle from "../../../CSS/Base.module.css";
import {
  SectionTitle,
  StyledOneChildContainer,
  StyledParentContainer,
  StyledTwoChildContainer,
  SubSectionTitle,
} from "../../StyledComponents/Container";
import CommonUtilities from "../../Utils/CommonUtilities";
import { formatDateTime, formatDateToMMDDYYYY } from "../../Utils/DataFormat";
import { notifyUser } from "../../Utils/notifyUser";
import { getClientDetailsByClientName } from "../../Utils/ClientSettingsTable";
import { setActiveIndex, setOpenMenuIndex } from "../../../../store/MenutItems/menuUiSlice";
import { useNavigate } from "react-router-dom";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "center" | "right" | "left";
  renderCell?: (row: any) => React.ReactNode;
}

const BlackBaudTransactionLog: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const translate = t as (key: string) => string;
  const userInfo = useSelector((state: RootState) => state.auth);
  const ClientDetails = getClientDetailsByClientName(userInfo?.clientVo?.name);
  const contactInformation = useSelector((state: RootState) => state.participantAdminProfile);
  const retain = useSelector((state: RootState) => state.table.blackBaudTransactionLog);
  const [openPostDailyTransactionsPopUp, setOpenPostDailyTransactionsPopUp] = useState(false);
  const [loadingFlag, setLoadingFlag] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [batchStatus, setBatchStatus] = useState(retain?.filters.batchStatus || "All");
  const [eventType, setEventType] = useState("");
  // use Dayjs for date state so .format() is available
  const [startDate, setStartDate] = useState<Dayjs | null>(
    retain?.filters?.startDate ? dayjs(retain.filters.startDate) : null
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(
    retain?.filters?.endDate ? dayjs(retain.filters.endDate) : null
  );
  const [searchFlag, setSearchFlag] = useState(0);
  const batchStatusOptions = CommonUtilities.batchStatusOptions;

  const columns: Column[] = [
    {
      id: "transactionDate",
      label: translate("transactionDate"),
      minWidth: 150,
      align: "center",
      renderCell: (row) => (
        <Link
          sx={{ cursor: "pointer", color: "#1976d2" }}
          onClick={() => alert(`Navigate to Batch Detail: ${row.batchId}`)}
        >
          {row.tranDate}
        </Link>
      ),
    },
    { id: "batchStatus", label: translate("batchStatus"), minWidth: 130, align: "center" },
    {
      id: "submitTime",
      label: translate("submitTime"),
      minWidth: 180,
      align: "center",
    },
  ];
  const handleSearch = () => {
    if (startDate && endDate) {
      const from = new Date(startDate.format("YYYY-MM-DD"));
      const to = new Date(endDate.format("YYYY-MM-DD"));
      if (to < from) {
        toast.error(translate("toDateShouldBeGreaterThanFromDate"));
        return;
      }
    }
    setPage(0);
    setSearchFlag((prev) => prev + 1);
  };

  const handlePostDailyTransactionsPopUpClose = () => {
    setOpenPostDailyTransactionsPopUp(false);
  };

  function handlePostDailyTransactionsClose() {
    setOpenPostDailyTransactionsPopUp(false);
  }
  function handleConfirmPostDailyTransactions() {
    handlePostDailyTransactionsClose();
    handleSearch();
  }
  const handlePostDailyTransactions = () => {
    setOpenPostDailyTransactionsPopUp(true);
  };

  const handleClear = () => {
    dispatch(resetTableState({ tableKey: "blackBaudTransactionLog" }));
    setBatchStatus("All");
    setEventType("");
    setStartDate(null);
    setEndDate(null);
    setPage(0);
    setSearchFlag((prev) => prev + 1);
  };
  const handleBack = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (contactInformation.participantAdminProfile === constants.ADMIN_VIEW) {
      dispatch(setActiveIndex(0));
      dispatch(setOpenMenuIndex(null));
      if ((ClientDetails?.name === constants.CLIENT_NAME_DC_GARY && userInfo?.roles?.roleName === constants.ROLES_ADMIN_MANAGER)) {
        navigate(constants.USER_LISTING_PATH);
      } else {
        navigate("/rcboSummary");
      }
    }
  };

  const getTransactionData = async () => {
    const vo = {
      startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
      endDate: endDate ? endDate.format("YYYY-MM-DD") : null,
      eventType: eventType || null,
      status: batchStatus && batchStatus !== "All" ? batchStatus : null,
      page: page + 1,
      size: rowsPerPage,
    };

    setLoading(true);
    setRows([]);
    try {
      dispatch(
        setTableFilters({
          tableKey: "blackBaudTransactionLog",
          filters: {
            // store filter values as strings (or null) for persistence
            startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
            endDate: endDate ? endDate.format("YYYY-MM-DD") : null,
            batchStatus,
          },
        })
      );
      dispatch(
        setPagination({
          tableKey: "blackBaudTransactionLog",
          page,
          pageSize: rowsPerPage,
        })
      );
      const response = await axiosInterceptor.post(environment.postBlackBaudTransactionLogFilter, vo);

      if (response.status === constants.STATUS_CODES.SUCCESS && response.data?.data) {
        const transformedData = response.data.data.map((item: any) => ({
          ...item,
          submitTime: formatDateTime(item.submitTime),
          tranDate: item.transactionDate,
          transactionDate: formatDateToMMDDYYYY(item.transactionDate),
        }));
        setRows(transformedData);
        setCount(response.data.totalRecordCount || 0);
      } else {
        setRows([]);
        setCount(0);
      }
    } catch (error: any) {
      console.error("Error:", error);
      notifyUser(
        error?.response?.data?.message || translate("somethingWentWrongWhileFetchingData"),
        "error",
        translate("blackBaudTransaction")
      );
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getTransactionData();
  }, [page, rowsPerPage, searchFlag]);

  return (
    <StyledParentContainer>
      <StyledOneChildContainer>
        <StyledTwoChildContainer sx={{ border: "none" }}>
          <SectionTitle>{translate("blackBaudTransaction")}</SectionTitle>

          {/* Filters */}
          <Box
            sx={{
              px: 1.5,
              mt: 2,
              display: "flex",
              gap: 2,
              alignItems: "center",
              // allow wrapping on very small screens
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            {/* left group: filters + search/clear */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
                flex: "1 1 auto",
              }}
            >
              <CustomAutocomplete
                label={translate("batchStatus")}
                size="small"
                sx={{ width: { xs: "100%", sm: 220 } }}
                options={batchStatusOptions}
                value={batchStatus}
                onChange={(_, newValue) => {
                  if (!newValue) return;
                  setBatchStatus(newValue || "");
                  setPage(0);
                  setSearchFlag((prev) => prev + 1);
                }}
              />

              <CustomDatePicker
                sx={{ width: { xs: "100%", sm: 220 } }}
                label={translate("fromDate")}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                size="small"
              />

              <CustomDatePicker
                sx={{ width: { xs: "100%", sm: 220 } }}
                label={translate("toDate")}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                size="small"
              />

              <ContainedButton onClick={handleSearch}>{translate("search")}</ContainedButton>
              <OutlinedButton onClick={handleClear}>{translate("clear")}</OutlinedButton>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                minWidth: { xs: "100%", sm: "auto" },
              }}
            >
              <ContainedButton onClick={handlePostDailyTransactions}>
                {translate("postDailyTransactions")}
              </ContainedButton>
            </Box>
          </Box>

          {/* Table */}
          <Box sx={{ width: "100%", px: 1.5, mt: 3 }}>
            <ReusableTable
              tableKey="blackBaudTransactionLog"
              columns={columns}
              rows={rows}
              count={count}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(newPage) => setPage(newPage)}
              onRowsPerPageChange={(newRowsPerPage) => {
                setRowsPerPage(newRowsPerPage);
                setPage(0);
              }}
              loading={loading}
            />
            <div className={basestyle.buttoncontainer}>
              <ContainedButton onClick={handleBack}>
                {" "}
                {translate("back")}{" "}
              </ContainedButton>
            </div>
          </Box>
          <PostDailyTransactionsPopUp
            open={openPostDailyTransactionsPopUp}
            handleClose={handlePostDailyTransactionsPopUpClose}
            saveChanges={() => handleConfirmPostDailyTransactions()}
          />
          <Backdrop
            sx={{
              color: "#fff",
              zIndex: (theme) => theme.zIndex.drawer + 1,
              display: "flex",
              flexDirection: "column",
            }}
            open={loadingFlag}
          >
            <CircularProgress color="inherit" />
            <SubSectionTitle>{translate("pleasewait")}</SubSectionTitle>
          </Backdrop>
        </StyledTwoChildContainer>
      </StyledOneChildContainer>
    </StyledParentContainer>
  );
};

export default BlackBaudTransactionLog;
